import { CODE_FLAG_DEFAULTS } from '@/lib/config/flag-defaults';
// 부정공 TALK(오픈채팅) 진입 슬롯 공용 모듈.
//
// 카톡 전환 계측은 user_events 한 곳으로 통일한다.
// conversion_events(cta_view/cta_click)와 혼용하지 말 것 — 두 테이블에 나뉘면
// 슬롯별 클릭률을 한 쿼리로 못 낸다.
//
// ⚠️ 구조적 한계: 오픈채팅 URL 은 쿼리 파라미터를 받지 못한다.
//    방에 들어온 사람이 어느 현장에서 왔는지는 카더라 쪽에서 알 수 없고,
//    계측은 '클릭 시점'까지가 한계다. 그 이후 연결은 방 운영(코드 밖) 영역.
//    이 한계 때문에 리드폼(누가·어느 현장인지 남는 유일한 경로)을 없애면 안 된다.

import { track } from '@/lib/analytics';

export const KAKAO_TALK_URL = 'https://open.kakao.com/o/gk8TBGyh';

/**
 * 오픈채팅 참여자 수 — «하한 표기» 문자열.
 *
 * 하한 표기 — 실측 1,240(2026-07-18) 기준, 수동 갱신 불필요 설계 (HC_CLOSE_20260913 K-2 · C-5 ㄴ).
 * 카카오가 참여자 수 API 를 주지 않아 정확 숫자는 반드시 낡는다. 그래서 «넘었다고 확인된 선»만 쓴다.
 *
 * ⛔ 숫자 상수로 되돌리지 말 것. 숫자가 있으면 누군가 toLocaleString 으로 정확 숫자를 다시 렌더한다.
 *    이 모듈은 표시 문자열만 내보낸다.
 */
export const TALK_MEMBER_LABEL = '1,200+';

/**
 * RD-3 C6 — 참여 인원 표시 스위치(기본 off). 실제 값을 받을 경로가 없어 「1,200+명」 문구를 걷었다.
 * Node 가 확인된 값을 주면 TALK_MEMBER_LABEL 을 그 값으로 바꾸고 이 상수 한 곳만 true 로 켠다.
 */
export const TALK_MEMBER_SHOW = CODE_FLAG_DEFAULTS.talk_member_show;
/** 인원 접두 구절(「1,200+명이 」) — 스위치가 꺼져 있으면 빈 문자열. */
export const TALK_MEMBER_PREFIX = TALK_MEMBER_SHOW ? `${TALK_MEMBER_LABEL}명이 ` : '';

/**
 * 배너 슬롯. 어느 자리가 실제로 방으로 사람을 보내는지 판정하는 축이다.
 *   sticky       전역 상단 고정 배너
 *   site_cta     현장 상세 6번 블록 — 현장 맥락 텍스트 CTA
 *   supply_table 공급/위치 정보 표의 `미공개` 칸 인라인 링크
 *   bottom_bar   모바일 하단 고정 바
 *   rail         현장 상세 데스크탑 우측 레일 (v3 커밋4 · ≥1024px 에서만 렌더)
 *   faq          FAQ 마지막 항목
 *   inline       블로그 본문 이미지 배너 — MASTER v3 §2(2026-09-26) 이후 «블로그 글 끝» 전용
 *   fab          우하단 플로팅 버튼 (UI_INSTRUCTION_20260910 §1.B)
 *   footer       전 페이지 푸터 — MASTER v3 §2(2026-09-26) 신설. sticky(전역 상단 고정 배너)를
 *                걷어내며 「단톡방 배너는 푸터 1곳 + 블로그 글 끝만」 규칙의 푸터 쪽 자리.
 *   topbar       전 페이지 상단 밴드(44px) — E-0(2026-09-26, FINAL_DESIGN_E §1) 복원.
 *                sticky 의 후신. 이벤트는 band_view/band_click(아래 trackBandView 참고).
 *
 * ⚠️ 새 진입점은 «새 이벤트명» 이 아니라 «새 슬롯» 으로 붙인다. 이벤트를 늘리면
 *    슬롯별 클릭률을 한 쿼리로 못 낸다 — 이 모듈 머리의 「user_events 한 곳으로 통일」이
 *    그 뜻이다. 유니온 타입이라 오타는 컴파일에서 걸린다.
 *
 * ⛔ 'sticky' 슬롯은 MASTER v3 §2(2026-09-26) 로 전 페이지 상단 고정 배너가
 *    제거되며 신규 발생이 끊겼다 — 과거 데이터 조회용으로 값만 남긴다.
 */
export type TalkSlot =
  | 'sticky'
  // 세션 A 19:35 — 현장 상세 리드폼 «접수 완료» 화면의 카톡방 버튼.
  | 'leadform_done'
  | 'site_cta'
  | 'supply_table'
  | 'bottom_bar'
  | 'rail'
  | 'faq'
  | 'inline'
  | 'fab'
  | 'footer'
  | 'topbar'
  // SP(2026-09-30) — 현장 연결 블로그·게시판 글의 하단 고정 도크(components/sp/SiteDock). FAB 자리를 대신한다.
  | 'dock'
  // v4 — 네이버 카페 글 머리·꼬리 → /go/kakao 리다이렉트(서버가 user_events 에 적재).
  | 'cafe'
  // retention-1031 — 현장 상세 「일정 알림 받기」 블록(components/apt/SiteAlertSubscribe)의 카톡방 버튼.
  //   네이버 인앱(웹 푸시 불가 · 사람 조회의 46%)에서는 이 버튼이 주 버튼이다.
  | 'site_alert';

export type TalkTrackProps = {
  /** 어느 현장이 방으로 사람을 보내는지 — 핵심 지표. 현장 밖 슬롯은 생략. */
  site_slug?: string;
  [k: string]: unknown;
};

/** 노출은 세션 × 슬롯 × 현장 단위로 1회만 센다. 같은 세션에서 현장을 옮기면 다시 센다. */
function viewKey(slot: TalkSlot, siteSlug?: string) {
  return `kd_bview:${slot}:${siteSlug || '-'}`;
}

/**
 * 배너 노출. 뷰포트 진입 시 1회 호출한다.
 * 노출 이벤트가 없으면 클릭률을 계산할 수 없다 — 클릭만 쌓으면 슬롯 비교가 불가능하다.
 */
export function trackTalkView(slot: TalkSlot, props: TalkTrackProps = {}) {
  if (typeof window === 'undefined') return;
  const key = viewKey(slot, props.site_slug as string | undefined);
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    /* 프라이빗 모드 등 — 중복 제거를 포기하고 계측은 계속한다 */
  }
  track('banner_view', 'bujeonggong_talk', {
    slot,
    page_path: window.location.pathname,
    ...props,
  });
}

/** 배너 클릭. 중복 제거하지 않는다 — 재클릭도 신호다. */
export function trackTalkClick(slot: TalkSlot, props: TalkTrackProps = {}) {
  if (typeof window === 'undefined') return;
  track('banner_click', 'bujeonggong_talk', {
    slot,
    page_path: window.location.pathname,
    ...props,
  });
}

// ── E-0 상단 밴드(topbar) ────────────────────────────────────────────────────
//
// FINAL_DESIGN_E §1(2026-09-26) — ffe76d23 가 걷어낸 전 페이지 상단 띠를 44px 로 복원.
//
// ⚠️ 이벤트명 예외: 위 TalkSlot 주석의 「새 진입점은 새 슬롯으로」 규칙과 달리
//    밴드는 banner_view/banner_click 이 아니라 «band_view/band_click» 을 쓴다(E-0 지시).
//    대신 event_name('bujeonggong_talk')·properties.slot('topbar') 는 같은 축에 둔다 —
//    슬롯 비교는 `event_type in ('banner_view','band_view')` 한 쿼리로 합칠 수 있다.
// ⚠️ 노출은 «페이지뷰당 1회» 다(세션당 1회인 trackTalkView 와 다르다). 밴드는 모든
//    페이지에 있으니 세션 dedupe 를 걸면 첫 페이지 한 번만 세고 클릭률 분모가 무너진다.
//    페이지뷰 경계는 호출자(TopBandTracker)가 pathname 으로 판정한다.

/** 밴드 링크 utm — FINAL_DESIGN_E §1-4 정본값(2026-09-26). ⛔ 임의로 바꾸지 말 것(문서 개정으로만).
 *  오픈채팅은 쿼리를 무시하고 같은 방을 연다(2026-09-26 실측: 파라미터 유무 모두 200·동일 본문).
 *  카카오 쪽에서 utm 을 읽을 방법은 없다 — 이 파라미터는 공유·전달된 링크의 출처 식별용이다. */
export const TOPBAR_UTM = 'utm_source=topbar&utm_medium=band&utm_campaign=bjg_kakao';
export const TOP_BAND_HREF = `${KAKAO_TALK_URL}?${TOPBAR_UTM}`;

/** 밴드 노출 — 페이지뷰당 1회(호출자가 보장). */
export function trackBandView() {
  if (typeof window === 'undefined') return;
  track('band_view', 'bujeonggong_talk', {
    slot: 'topbar' satisfies TalkSlot,
    page_path: window.location.pathname,
  });
}

/** 밴드 클릭 — 중복 제거하지 않는다. */
export function trackBandClick() {
  if (typeof window === 'undefined') return;
  track('band_click', 'bujeonggong_talk', {
    slot: 'topbar' satisfies TalkSlot,
    page_path: window.location.pathname,
  });
}

// ── retention-1031 — 현장 맞춤 카톡방 문구 · 지면 정리 ─────────────────────────
//
// 30일 실측(user_events · banner_view→banner_click · 10/31): site_cta 상세 2,430→38 · 블로그 331→1 · dock 1,676→22 ·
//   faq 184→11 · bottom_bar 972→7 · supply_table 522→3 · rail 347→2 · fab 854→0 · leadform_done 43→7 · inline(지역 문서) 87→3.
//   fab·rail·bottom_bar·supply_table·faq·dock 은 이미 화면에서 걷혔다(10/11~10/27 · 마지막 이벤트는 캐시 페이지).
//   «일반 문구» 블로그 끝 버튼은 0.3% — 현장 이름이 없는 글에서는 그리지 않는다(blog/[slug] 호출부).
/** 측정 결과 걷은 슬롯(과거 데이터 조회용 값만 남는다). 다시 붙이려면 새 근거가 먼저다. */
export const TALK_RETIRED_SLOTS: readonly TalkSlot[] = ['sticky', 'fab', 'rail', 'bottom_bar', 'supply_table', 'topbar'];

/** 일반 문구(현장 이름을 모를 때). */
export const TALK_GENERIC_LABEL = '카카오톡 분양 정보방 입장';

/** 순수 — 「{현장명} 소식 카톡방에서 받기」. 이름이 비면 일반 문구 · 너무 길면 줄임(버튼 한 줄). */
export function talkSiteLabel(siteName: string | null | undefined): string {
  const n = String(siteName ?? '').replace(/\s+/g, ' ').trim();
  if (!n) return TALK_GENERIC_LABEL;
  const short = n.length > 18 ? `${n.slice(0, 17)}…` : n;
  return `${short} 소식 카톡방에서 받기`;
}
